import type { FieldValue, FormField, ValidationRules } from "./types";
import { isFieldVisible } from "./visibilityRules";

const asRules = (field: FormField): ValidationRules | null =>
  (field.validation_rules as ValidationRules | null) ?? null;

function buildValuesByKey(fields: FormField[], values: Record<number, FieldValue>): Record<string, string> {
  const idToKey: Record<number, string> = {};
  fields.forEach((f) => (idToKey[f.id] = f.key));
  const map: Record<string, string> = {};
  Object.entries(values).forEach(([id, fv]) => {
    const key = idToKey[Number(id)];
    if (key) map[key] = fv.value ?? "";
  });
  return map;
}

export function visibleFieldsOf(
  fields: FormField[],
  values: Record<number, FieldValue>
): FormField[] {
  const valuesByKey = buildValuesByKey(fields, values);
  return fields.filter((f) =>
    isFieldVisible(f.visibility_rules, valuesByKey)
  );
}

export function hasAnswer(fv: FieldValue | undefined): boolean {
  if (!fv) return false;
  if (fv.value_options && fv.value_options.length > 0) return true;
  return fv.value != null && fv.value !== "";
}

function parseDateYMD(raw: string): Date | null {
  const parts = raw.split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, m, d] = parts;
  const date = new Date(y, m - 1, d);
  date.setHours(0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
}

function addUnits(date: Date, amount: number, unit: string): Date {
  const d = new Date(date);
  if (unit === "days") d.setDate(d.getDate() + amount);
  else if (unit === "months") d.setMonth(d.getMonth() + amount);
  else if (unit === "years") d.setFullYear(d.getFullYear() + amount);
  return d;
}

function todayMidnight(): Date {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
}

export function validateField(
  field: FormField,
  value: FieldValue | undefined,
  fields: FormField[],
  values: Record<number, FieldValue>,
  opts: { requireRequired?: boolean } = {}
): string | null {
  const requireRequired = opts.requireRequired ?? true;
  const valuesByKey = buildValuesByKey(fields, values);
  if (!isFieldVisible(field.visibility_rules, valuesByKey)) {
    return null;
  }

  if (requireRequired && field.is_required && !hasAnswer(value)) {
    return "Campo obrigatório.";
  }

  const raw = value?.value ?? "";
  if (!raw) return null;

  const rules = asRules(field);

  switch (field.field_type) {
    case "date": {
      const dateVal = parseDateYMD(raw);
      if (!dateVal) return "Data inválida.";

      const today = todayMidnight();

      if (rules?.min_date) {
        const minD = rules.min_date === "today" ? today : parseDateYMD(rules.min_date);
        if (minD && dateVal < minD) {
          const label = rules.min_date === "today" ? "hoje" : rules.min_date;
          return `A data deve ser igual ou posterior a ${label}.`;
        }
      }

      if (rules?.max_date) {
        const maxD = rules.max_date === "today" ? today : parseDateYMD(rules.max_date);
        if (maxD && dateVal > maxD) {
          const label = rules.max_date === "today" ? "hoje" : rules.max_date;
          return `A data deve ser igual ou anterior a ${label}.`;
        }
      }

      const unitPt: Record<string, string> = { days: "dia(s)", months: "mês/meses", years: "ano(s)" };

      if (rules?.min_from_today) {
        const { amount, unit } = rules.min_from_today;
        if (typeof amount === "number" && amount >= 0 && ["days", "months", "years"].includes(unit)) {
          const windowMin = addUnits(today, amount, unit);
          if (dateVal < windowMin) {
            const label = amount === 0 ? "hoje" : `${amount} ${unitPt[unit] ?? ""}`;
            return `A data deve ter no mínimo ${label} de antecedência a partir de hoje.`;
          }
        }
      }

      if (rules?.max_from_today) {
        const { amount, unit } = rules.max_from_today;
        if (typeof amount === "number" && amount >= 0 && ["days", "months", "years"].includes(unit)) {
          if (dateVal < today) return "A data deve ser a partir de hoje.";
          const windowMax = addUnits(today, amount, unit);
          if (dateVal > windowMax) {
            const label = amount === 0 ? "hoje" : `${amount} ${unitPt[unit] ?? ""} após hoje`;
            return `A data deve ser no máximo ${label}.`;
          }
        }
      }
      return null;
    }

    case "number":
    case "currency": {
      const num = Number(raw);
      if (Number.isNaN(num)) return "Valor inválido.";
      if (rules?.integer_only && num !== Math.trunc(num)) return "O valor deve ser um número inteiro.";
      if (rules?.min != null && num < rules.min) return `O valor deve ser igual ou maior que ${rules.min}.`;
      if (rules?.max != null && num > rules.max) return `O valor deve ser igual ou menor que ${rules.max}.`;
      return null;
    }

    default:
      return null;
  }
}

export interface FormValidationResult {
  isValid: boolean;
  errors: Record<number, string>;
}

export function validateForm(
  fields: FormField[],
  values: Record<number, FieldValue>,
  opts: { requireRequired?: boolean } = {}
): FormValidationResult {
  const errors: Record<number, string> = {};
  for (const field of visibleFieldsOf(fields, values)) {
    const err = validateField(field, values[field.id], fields, values, opts);
    if (err) errors[field.id] = err;
  }
  return { isValid: Object.keys(errors).length === 0, errors };
}

export function validateFile(field: FormField, file: File): string | null {
  const rules = asRules(field);
  if (rules?.max_size_bytes && file.size > rules.max_size_bytes) {
    const mb = rules.max_size_bytes / 1024 / 1024;
    return `Arquivo excede o tamanho máximo permitido (${Math.round(mb)}MB).`;
  }
  if (rules?.accepted_formats && rules.accepted_formats.length > 0) {
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    const accepted = rules.accepted_formats.map((f) => f.toLowerCase());
    if (!accepted.includes(ext)) {
      return `Formato não permitido. Aceitos: ${rules.accepted_formats.join(", ")}.`;
    }
  }
  return null;
}